import React, { useMemo } from 'react'
import { cn } from '@/lib/utils'

interface Season {
  id: number
  name: string
  startDate: string
  endDate: string
  modifierType: string
  modifierValue: number
  priority: number
}

interface SeasonCalendarPreviewProps {
  seasons: Season[]
  currencySymbol?: string
}

const SEASON_COLORS = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
]

const SEASON_DOT_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-indigo-500',
]

const SEASON_BG_COLORS = [
  'bg-blue-50',
  'bg-emerald-50',
  'bg-amber-50',
  'bg-purple-50',
  'bg-pink-50',
  'bg-teal-50',
  'bg-orange-50',
  'bg-indigo-50',
]

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfWeek(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay()
  return day === 0 ? 6 : day - 1
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function isToday(year: number, month: number, day: number): boolean {
  const today = new Date()
  return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day
}

function formatModifier(s: Season, sym: string): string {
  if (s.modifierType === 'percentage') {
    return `${s.modifierValue >= 0 ? '+' : ''}${s.modifierValue}%`
  }
  return `${s.modifierValue >= 0 ? '+' : '-'}${sym}${Math.abs(s.modifierValue)}`
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export default function SeasonCalendarPreview({ seasons, currencySymbol: sym = '£' }: SeasonCalendarPreviewProps) {

  // 12 months starting from current month
  const months = useMemo(() => {
    const now = new Date()
    return Array.from({ length: 12 }, (_, offset) => {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
      return { year: d.getFullYear(), month: d.getMonth() }
    })
  }, [])

  // Stable color assignment by season id
  const colorMap = useMemo(() => {
    const map = new Map<number, number>()
    seasons.forEach((s, i) => {
      map.set(s.id, i % SEASON_COLORS.length)
    })
    return map
  }, [seasons])

  // Pre-compute which seasons apply to each date, sorted by priority (highest first)
  const dateLookup = useMemo(() => {
    const lookup = new Map<string, { indices: number[]; winnerIdx: number }>()

    for (const { year, month } of months) {
      const daysInMonth = getDaysInMonth(year, month)
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = formatDate(year, month, day)
        const dateVal = new Date(year, month, day)

        const matchingIndices: number[] = []
        seasons.forEach((s, idx) => {
          const start = new Date(s.startDate)
          const end = new Date(s.endDate)
          if (dateVal >= start && dateVal <= end) {
            matchingIndices.push(idx)
          }
        })

        if (matchingIndices.length > 0) {
          // Sort by priority descending — highest priority wins
          matchingIndices.sort((a, b) => seasons[b].priority - seasons[a].priority)
          lookup.set(dateStr, { indices: matchingIndices, winnerIdx: matchingIndices[0] })
        }
      }
    }

    return lookup
  }, [months, seasons])

  if (seasons.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-white p-8 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">No seasons defined. Add a season to see the calendar preview.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground">Calendar Preview</h3>

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        {seasons.map((s) => {
          const colorIdx = colorMap.get(s.id) ?? 0
          return (
            <span
              key={s.id}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                SEASON_COLORS[colorIdx]
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', SEASON_DOT_COLORS[colorIdx])} />
              {s.name}
              <span className="font-normal opacity-70">P{s.priority}</span>
            </span>
          )
        })}
      </div>

      {/* Calendar grid — 4 rows of 3 months */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {months.map(({ year, month }) => {
          const daysInMonth = getDaysInMonth(year, month)
          const firstDay = getFirstDayOfWeek(year, month)

          return (
            <div key={`${year}-${month}`} className="rounded-xl border border-border/60 bg-white p-3 shadow-sm">
              <div className="mb-2 text-center">
                <span className="text-xs font-bold text-foreground">{MONTH_NAMES[month]}</span>
                <span className="ml-1 text-xs text-muted-foreground">{year}</span>
              </div>
              <div className="grid grid-cols-7 gap-px text-center text-xs">
                {DAY_HEADERS.map((d) => (
                  <div key={d} className="py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {d}
                  </div>
                ))}
                {Array.from({ length: firstDay }, (_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1
                  const dateStr = formatDate(year, month, day)
                  const entry = dateLookup.get(dateStr)
                  const today = isToday(year, month, day)

                  // Color comes from the winning (highest priority) season
                  const winnerColor = entry
                    ? SEASON_BG_COLORS[colorMap.get(seasons[entry.winnerIdx].id) ?? 0]
                    : ''

                  const matchingSeasons = entry ? entry.indices.map((idx) => seasons[idx]) : []
                  const winner = entry ? seasons[entry.winnerIdx] : null

                  return (
                    <div
                      key={day}
                      className={cn(
                        'group/day relative flex h-7 items-center justify-center rounded text-[11px] transition-colors cursor-default',
                        entry ? `font-semibold ${winnerColor}` : 'text-muted-foreground/60',
                        today && 'ring-2 ring-primary/40'
                      )}
                    >
                      {day}
                      {entry && entry.indices.length > 1 && (
                        <div className="absolute bottom-0 flex gap-px">
                          {entry.indices.slice(0, 3).map((idx) => {
                            const colorIdx = colorMap.get(seasons[idx].id) ?? 0
                            return (
                              <span
                                key={idx}
                                className={cn('h-1 w-1 rounded-full', SEASON_DOT_COLORS[colorIdx])}
                              />
                            )
                          })}
                        </div>
                      )}
                      {/* Hover tooltip — positioned relative to the day cell */}
                      {entry && (
                        <div className="invisible group-hover/day:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                          <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg text-xs space-y-1.5 min-w-[180px] whitespace-nowrap">
                            {matchingSeasons.map((s) => {
                              const colorIdx = colorMap.get(s.id) ?? 0
                              const isWinner = s.id === winner?.id
                              return (
                                <div key={s.id} className={cn('flex items-center justify-between gap-3', !isWinner && matchingSeasons.length > 1 && 'opacity-50')}>
                                  <div className="flex items-center gap-1.5">
                                    <span className={cn('h-2 w-2 rounded-full', SEASON_DOT_COLORS[colorIdx])} />
                                    <span className={cn('font-medium', isWinner && 'text-foreground')}>
                                      {s.name}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5 tabular-nums">
                                    <span className={cn(
                                      'font-semibold',
                                      s.modifierValue > 0 ? 'text-emerald-600' : s.modifierValue < 0 ? 'text-rose-600' : 'text-muted-foreground'
                                    )}>
                                      {formatModifier(s, sym)}
                                    </span>
                                    <span className="text-muted-foreground/60">P{s.priority}</span>
                                  </div>
                                </div>
                              )
                            })}
                            {matchingSeasons.length > 1 && winner && (
                              <div className="border-t border-border/40 pt-1 text-[10px] text-muted-foreground">
                                <span className="font-medium text-foreground">{winner.name}</span> wins (highest priority)
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

    </div>
  )
}
